<template>
    <div class="DocumentDetailsModalHeader">
        <DocumentDetailsPagination
            v-if="tabsOverflow && documents.length > 1"
            :current-index="currentIndex"
            :total-count="documents.length"
            @previous="$emit('previous')"
            @next="$emit('next')"
        />

        <span
            v-if="requirementLabel && !tabsOverflow"
            ref="requirementName"
            class="DocumentDetailsModalHeader__requirementName"
        >
            {{ requirementLabel }}
        </span>

        <DocumentDetailsTabs
            ref="tabsContainer"
            :documents="documents"
            :current-index="currentIndex"
            :hidden="tabsOverflow"
            @select="handleSelectDocument"
        />

        <DocumentDetailsHeaderActions
            ref="headerActions"
            :documents="documents"
            :uploaded-at="uploadedAt"
            :email="email"
            :show-upload-date="!tabsOverflow"
            @download="$emit('download', $event)"
        >
            <template #header-actions>
                <slot name="header-actions" />
            </template>
        </DocumentDetailsHeaderActions>
    </div>
</template>

<style lang="scss">
.DocumentDetailsModalHeader {
    // Use display: contents to make this wrapper "disappear" from flex layout
    // This allows children to participate directly in the parent's flex container
    display: contents;

    &__requirementName {
        font-size: 14px;
        font-weight: 500;
        color: var(--eidQualityBlack);
        white-space: nowrap;
        flex-shrink: 0;
        padding-left: 20px;
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';

    import {
        DocumentOverflowDetection,
    } from '../utils/documentOverflowDetection.util';

    import DocumentDetailsHeaderActions from './DocumentDetailsHeaderActions.vue';
    import DocumentDetailsPagination from './DocumentDetailsPagination.vue';
    import DocumentDetailsTabs from './DocumentDetailsTabs.vue';

    @Component({
        emits: [ 'download', 'next', 'previous', 'select-document' ],
        components: {
            DocumentDetailsPagination,
            DocumentDetailsTabs,
            DocumentDetailsHeaderActions,
        },
    })
    export default class DocumentDetailsModalHeader extends Vue {
        @Prop({ type: Array, required: true })
        private documents!: VerificationDocument[];

        @Prop({ type: Number, required: true })
        private currentIndex!: number;

        @Prop({ type: String })
        private requirementLabel!: string;

        @Prop({ type: String })
        private uploadedAt!: string;

        @Prop({ type: String })
        private email!: string;

        private tabsOverflow = false;
        private overflowDetector: DocumentOverflowDetection | null = null;

        @Watch('documents', { immediate: true })
        private onDocumentsChange(): void {
            if (this.overflowDetector) {
                this.overflowDetector.checkTabsOverflow();
            }
        }

        private getDocumentName(index: number): string {
            return `File ${index + 1}`;
        }

        private handleSelectDocument(index: number): void {
            this.$emit('select-document', index);
        }

        private mounted(): void {
            // Wait for modal to be fully rendered
            this.$nextTick(() => {
                // Additional delay to ensure modal animation is complete
                setTimeout(() => {
                    this.overflowDetector = new DocumentOverflowDetection({
                        getTabsContainer: () => {
                            const tabsComponent = this.$refs.tabsContainer as Vue | undefined;
                            return tabsComponent?.$el as HTMLElement | undefined;
                        },
                        getHeaderRight: () => {
                            const headerActions = this.$refs.headerActions as Vue | undefined;
                            return headerActions?.$el as HTMLElement | undefined;
                        },
                        getUploadDate: () => {
                            const headerActions = this.$refs.headerActions as Vue | undefined;
                            if (headerActions) {
                                return (headerActions.$refs.uploadDate as HTMLElement | undefined);
                            }
                            return undefined;
                        },
                        getRequirementName: () => this.$refs.requirementName as HTMLElement | undefined,
                        documents: this.documents,
                        getDocumentName: (index) => this.getDocumentName(index),
                        onOverflowChange: (isOverflow) => {
                            this.tabsOverflow = isOverflow;
                        },
                    });
                    this.overflowDetector.measureUploadDateWidth();
                    this.overflowDetector.measureRequirementNameWidth();
                    this.overflowDetector.setup();
                    // Also listen to window resize
                    window.addEventListener('resize', this.overflowDetector.handleWindowResize);
                }, 100);
            });
        }

        private beforeUnmount(): void {
            if (this.overflowDetector) {
                this.overflowDetector.cleanup();
                window.removeEventListener('resize', this.overflowDetector.handleWindowResize);
            }
        }
    }
</script>
